'use client';

import React, { useContext } from 'react';
import type { DialogProps as AriaDialogProps, ModalOverlayProps as AriaModalOverlayProps } from 'react-aria-components';
import {
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    Modal as AriaModal,
    ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/isReactComponent';
import { CloseButton, CloseButtonProps } from '../button/CloseButton';
import { ModalHeader } from './ModalHeader';

const SEMANTIC_NAME_MODAL_OVERLAY = 'RomaModalOverlay';
const SEMANTIC_NAME_MODAL = 'RomaModal';
const SEMANTIC_NAME_DIALOG = 'RomaDialog';
const SEMANTIC_NAME_DIALOG_CONTENT = 'RomaDialogContent';

const DialogTrigger = AriaDialogTrigger;

const ModalContext = React.createContext<ModalOverlayProps>({});

export type ModalOverlayProps = {
    backgroundStyle?: 'blur' | 'dim' | 'solid' | 'none';
    transitionStyle?: 'fade' | 'none';
} & AriaModalOverlayProps;

const overlayStyles = {
    backgroundStyles: {
        blur: 'bg-bg-overlay-alpha backdrop-blur-sm',
        dim: 'bg-bg-overlay-alpha',
        solid: 'bg-bg-overlay-solid',
        none: 'bg-transparent',
    },
};

const ModalOverlay = ({
    transitionStyle = 'fade',
    backgroundStyle = 'dim',
    children,
    ...overlayProps
}: ModalOverlayProps) => {
    const handleOverlayPointerDownCapture = overlayProps.onPointerDownCapture;

    /**
     * Allows the active input to blur on backdrop clicks while keeping focus inside the modal.
     * React Aria immediately restores focus to satisfy focus containment, so we redirect focus
     * to a safe target in the dialog to prevent the blur from being undone.
     */
    const handlePointerDownCapture: React.PointerEventHandler<HTMLDivElement> = (event) => {
        handleOverlayPointerDownCapture?.(event);

        if (event.target !== event.currentTarget) return;

        const activeElement = document.activeElement;

        if (activeElement instanceof HTMLElement) {
            activeElement.blur();
        }

        const focusTarget = event.currentTarget.querySelector<HTMLElement>('[data-roma-dialog-focus]');
        focusTarget?.focus({ preventScroll: true });
    };

    return (
        <ModalContext.Provider
            value={{
                transitionStyle,
                backgroundStyle,
                ...overlayProps,
            }}
        >
            <AriaModalOverlay
                {...overlayProps}
                onPointerDownCapture={handlePointerDownCapture}
                className={(state) =>
                    cx(
                        SEMANTIC_NAME_MODAL_OVERLAY,
                        // layout and positioning
                        'fixed inset-0 z-(--z-index-modal) h-(--visual-viewport-height) min-h-(--page-height) flex w-full',
                        // scrolling and overflow
                        'overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]',
                        // padding and alignment
                        'items-end justify-center bg-overlay/70 px-4 pt-4 pb-[clamp(16px,8vh,64px)] outline-hidden',
                        // responsive adjustments
                        'sm:items-center sm:justify-center sm:p-8',
                        overlayStyles.backgroundStyles[backgroundStyle],
                        transitionStyle === 'fade' && state.isEntering && 'duration-300 ease-out animate-in fade-in',
                        transitionStyle === 'fade' && state.isExiting && 'duration-200 ease-in animate-out fade-out',
                        typeof overlayProps.className === 'function'
                            ? overlayProps.className(state)
                            : overlayProps.className,
                    )
                }
            >
                {children}
            </AriaModalOverlay>
        </ModalContext.Provider>
    );
};

ModalOverlay.displayName = SEMANTIC_NAME_MODAL_OVERLAY;

const ModalRoot = (props: AriaModalOverlayProps) => {
    const { transitionStyle } = useContext(ModalContext);

    return (
        <AriaModal
            {...props}
            className={(state) =>
                cx(
                    SEMANTIC_NAME_MODAL,
                    'pointer-events-none max-h-[calc(var(--visual-viewport-height)-var(--spacing-7xl))] w-full max-w-[calc(100vw-var(--spacing-7xl))] align-middle outline-hidden select-none overflow-y-auto overflow-visible max-sm:rounded-xl',
                    transitionStyle === 'fade' && state.isEntering && 'duration-300 ease-out animate-in zoom-in-95',
                    transitionStyle === 'fade' && state.isExiting && 'duration-200 ease-in animate-out zoom-out-95',
                    typeof props.className === 'function' ? props.className(state) : props.className,
                )
            }
        />
    );
};

ModalRoot.displayName = SEMANTIC_NAME_MODAL;

const Dialog = (props: AriaDialogProps) => (
    <AriaDialog
        {...props}
        className={cx(SEMANTIC_NAME_DIALOG, 'flex w-full items-center justify-center outline-hidden', props.className)}
    />
);

Dialog.displayName = SEMANTIC_NAME_DIALOG;

export type DialogContentProps = {
    children?: React.ReactNode;
    closeButton?: boolean | React.ReactNode;
    closeButtonSize?: CloseButtonProps['size'];
} & React.HTMLAttributes<HTMLDivElement>;

const renderCloseButton = ({
    closeButton,
    closeButtonSize,
}: Pick<DialogContentProps, 'closeButton' | 'closeButtonSize'>): React.ReactNode => {
    if (!closeButton) return null;

    if (closeButton === true) {
        return <CloseButton size={closeButtonSize} label="Close modal" slot="close" />;
    }

    if (React.isValidElement(closeButton)) {
        return React.cloneElement(closeButton as React.ReactElement<{ slot?: string }>, { slot: 'close' });
    }

    if (isReactComponent(closeButton)) {
        const CloseButtonComponent = closeButton;
        return <CloseButtonComponent slot="close" />;
    }

    return null;
};

const DialogContent = ({
    children,
    className,
    closeButton,
    closeButtonSize = 'md',
    tabIndex,
    ...props
}: DialogContentProps) => {
    const resolvedCloseButton = renderCloseButton({
        closeButton,
        closeButtonSize,
    });

    return (
        <div
            {...props}
            data-roma-dialog-focus
            data-close-button={resolvedCloseButton ? true : undefined}
            tabIndex={tabIndex ?? -1}
            className={cx(
                SEMANTIC_NAME_DIALOG_CONTENT,
                'pointer-events-auto relative flex w-full max-w-md rounded-2xl bg-primary py-4xl px-5xl shadow-xl outline-0',
                className,
            )}
        >
            {children}
            {resolvedCloseButton && <div className="absolute right-3 top-3 z-10">{resolvedCloseButton}</div>}
        </div>
    );
};

DialogContent.displayName = SEMANTIC_NAME_DIALOG_CONTENT;

type ModalComponent = typeof ModalRoot & {
    Overlay: typeof ModalOverlay;
    Dialog: typeof Dialog;
    DialogContent: typeof DialogContent;
    DialogTrigger: typeof DialogTrigger;
    Header: typeof ModalHeader;
};

export const Modal = Object.assign(ModalRoot, {
    Overlay: ModalOverlay,
    Dialog,
    DialogContent,
    DialogTrigger,
    Header: ModalHeader,
}) as ModalComponent;
